import { zodResolver } from '@hookform/resolvers/zod';
import { type QuoteLine, type TimesheetDepartmentList } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MoneyInput,
  MultipleSelect,
  Textarea,
} from '@kalos/ui';
import { type FC, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useTimesheetDepartmentListQuery } from '../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { type ZodObjectWithModelKeys } from '../../../../tools/typeguargs';

// Schema and types
type FlatRateCreateFormFields = Pick<QuoteLine, 'description' | 'departments' | 'adjustment'>;

const flatRateCreateFormSchema = z.object({
  description: z
    .string()
    .max(1000, 'Description is too long (max 1000 characters)')
    .min(1, 'Description is required'),
  departments: z.array(z.number()).min(1, 'At least one department is required'),
  adjustment: z.coerce
    .number()
    .min(0.0, 'Cost must be greater than or equal to 0')
    .refine((val) => !isNaN(val), 'Cost must be a valid number')
    .transform((val) => Number(val.toFixed(2))),
  excludeInvoiceDiscount: z.boolean().default(false),
}) satisfies ZodObjectWithModelKeys<FlatRateCreateFormFields>;

type FlatRateCreateFormSchemaType = z.infer<typeof flatRateCreateFormSchema>;

// Form component
export const FlatRateCreateForm: FC<{
  onSave: (arg: { data: FlatRateCreateFormSchemaType }) => void;
  isLoading?: boolean;
  disabled?: boolean;
  canEditDepartment?: boolean;
  initialDepartment?: number;
}> = ({ onSave, disabled, isLoading, canEditDepartment = true, initialDepartment }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FlatRateCreateFormSchemaType>({
    resolver: zodResolver(flatRateCreateFormSchema),
    mode: 'onChange',
    defaultValues: {
      description: '',
      departments: [0],
      adjustment: 0,
      excludeInvoiceDiscount: false,
    },
    disabled: disabled || isLoading || isSubmitting,
  });

  // Update departments when initialDepartment changes
  useEffect(() => {
    if (initialDepartment && !canEditDepartment) {
      form.setValue('departments', [initialDepartment]);
    }
  }, [initialDepartment, canEditDepartment, form]);

  const handleSave: SubmitHandler<FlatRateCreateFormSchemaType> = async (data) => {
    if (isSubmitting || isLoading) return;

    try {
      setIsSubmitting(true);
      await onSave({ data });
    } finally {
      setIsSubmitting(false);
    }
  };

  const departmentsQuery = useTimesheetDepartmentListQuery({
    select: (data: TimesheetDepartmentList) => {
      const options = [
        { value: '0', label: 'None' },
        ...data.results.map((item) => ({
          value: item.id.toString(),
          label: `${item.value} - ${item.description}`,
        })),
      ];
      return options;
    },
  });

  return (
    <FormContext {...form}>
      <form onSubmit={form.handleSubmit(handleSave)} className="flex items-end gap-4">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} rows={5} disabled={isSubmitting || isLoading || disabled} />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="departments"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Department(s)</FormLabel>
              <FormControl>
                <MultipleSelect
                  options={departmentsQuery.data || []}
                  selected={field.value ? field.value.map(String) : []}
                  onChange={(values) => field.onChange(values.map(Number))}
                  placeholder="Select departments..."
                  loading={departmentsQuery.isLoading}
                  disabled={field.disabled || !canEditDepartment || isSubmitting || isLoading}
                />
              </FormControl>
              <FormMessage className="text-red-500" />
              {!canEditDepartment && (
                <div className="text-muted-foreground mt-1 text-sm">
                  Department editing requires permission
                </div>
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="adjustment"
          render={({ field }) => (
            <FormItem className="w-32">
              <FormLabel>Cost</FormLabel>
              <FormControl>
                <MoneyInput
                  {...field}
                  type="number"
                  step="0.01"
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                  }}
                  disabled={isSubmitting || isLoading || disabled}
                />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="excludeInvoiceDiscount"
          render={({ field }) => (
            <FormItem className="flex flex-col items-start">
              <FormLabel>Excl. Discount</FormLabel>
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting || isLoading || disabled}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          isLoading={isLoading || isSubmitting}
          disabled={disabled || isLoading || isSubmitting || !form.formState.isValid}
          className="h-10"
        >
          Save
        </Button>
      </form>
    </FormContext>
  );
};
